import { useState } from 'react';

import { Button } from '~/components/ui/button';
import type {
  ChangeLedgerRow,
  LedgerDemand,
  LedgerEvidence,
  SearchImpactSummary,
} from '~/features/catalog-operations/types/search-impact.types';
import { cn } from '~/utils/cn';

import {
  COVERAGE_COPY,
  LEDGER_FILTERS,
  REASON_COPY,
  REASON_TONE_STYLE,
  VERDICT_LABEL,
  VERDICT_STYLE,
  formatVolume,
  hasCommercialIntent,
  inLedgerFilter,
  opportunityOf,
  sortLedger,
  trendOf,
  type LedgerFilter,
} from './search-impact-utils';

interface Props {
  ledger: ChangeLedgerRow[];
  summary: SearchImpactSummary;
}

/** Desktop grid columns, shared by the header and every row so they line up. */
const GRID = 'md:grid-cols-[104px_1fr_1fr_1.25fr_1.6fr_96px]';

/** Source quote with the matched words emphasised, using the server's offsets. */
function Quote({ evidence }: { evidence: LedgerEvidence }) {
  const parts: { text: string; hit: boolean }[] = [];
  let cursor = 0;
  for (const [start, end] of [...evidence.matches].sort((a, b) => a[0] - b[0])) {
    if (start < cursor || end > evidence.span.length) continue;
    if (start > cursor) parts.push({ text: evidence.span.slice(cursor, start), hit: false });
    parts.push({ text: evidence.span.slice(start, end), hit: true });
    cursor = end;
  }
  if (cursor < evidence.span.length) parts.push({ text: evidence.span.slice(cursor), hit: false });
  return (
    <span className="text-muted-foreground mt-1.5 block text-xs leading-relaxed">
      {parts.map((part, i) =>
        part.hit ? (
          // Positional fragments of one quote — the index is the identity.
          <b key={i} className="text-foreground font-semibold">
            {part.text}
          </b>
        ) : (
          <span key={i}>{part.text}</span>
        ),
      )}
    </span>
  );
}

/** Small rounded label used for coverage / opportunity / trend / intent. */
function Chip({
  className,
  title,
  children,
}: {
  className: string;
  title?: string;
  children: string;
}) {
  return (
    <span
      title={title}
      className={cn('rounded-md px-1.5 py-0.5 text-[11px] font-semibold', className)}
    >
      {children}
    </span>
  );
}

/** One search the value serves: keyword + source, coverage and volume, then opportunity / trend / intent. */
function Serve({ demand, testId }: { demand: LedgerDemand; testId: string }) {
  const coverage = COVERAGE_COPY[demand.coverage];
  const opportunity = opportunityOf(demand);
  const trend = trendOf(demand);
  return (
    <div className="border-b border-dashed pb-2 last:border-b-0 last:pb-0" data-testid={testId}>
      <div className="text-sm font-semibold">
        {demand.keyword}{' '}
        <span className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">
          · {demand.source === 'product_descriptor' ? 'product descriptor' : 'category keyword'}
        </span>
      </div>
      <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-1.5 text-xs">
        <Chip className={coverage.className}>{demand.note ?? coverage.label}</Chip>
        <span className="tabular-nums" data-testid={`${testId}-volume-value`}>
          {demand.monthlyVolume === null
            ? '—'
            : `${demand.monthlyVolume.toLocaleString('en-US')}/mo`}
        </span>
        {demand.competitionIndex !== null && (
          <span>· advertiser competition {demand.competitionIndex}</span>
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <Chip className={opportunity.className}>{opportunity.label}</Chip>
        {trend && <Chip className={trend.className}>{trend.label}</Chip>}
        {hasCommercialIntent(demand) && (
          <Chip
            className="bg-warning-50 text-warning"
            title={`Top-of-page bid $${(demand.bidLow ?? 0).toFixed(2)} to $${(demand.bidHigh ?? 0).toFixed(2)}`}
          >
            Commercial intent
          </Chip>
        )}
      </div>
    </div>
  );
}

/** Right-aligned volume change: covered (green), lost (red), or no change. */
function VolumeChange({ value, testId }: { value: number; testId: string }) {
  if (value > 0) {
    return (
      <div
        className="text-success text-right text-sm font-semibold tabular-nums"
        data-testid={testId}
      >
        +{formatVolume(value)}
        <small className="text-muted-foreground block text-[11px] font-semibold">/mo covered</small>
      </div>
    );
  }
  if (value < 0) {
    return (
      <div
        className="text-error text-right text-sm font-semibold tabular-nums"
        data-testid={testId}
      >
        −{formatVolume(value)}
        <small className="text-muted-foreground block text-[11px] font-semibold">/mo lost</small>
      </div>
    );
  }
  return (
    <div
      className="text-muted-foreground text-right text-sm font-semibold tabular-nums"
      data-testid={testId}
    >
      0<small className="block text-[11px] font-semibold">no change</small>
    </div>
  );
}

/** Every attribute value enrichment touched: why it changed and the search demand it covers. */
export function ChangeLedger({ ledger, summary }: Props) {
  const [filter, setFilter] = useState<LedgerFilter>('all');
  const rows = sortLedger(ledger.filter((row) => inLedgerFilter(row, filter)));
  const { demandChange, attributes } = summary;
  const gainedRows = ledger.filter((row) => row.verdict === 'gained');
  const withDemand = gainedRows.filter((row) =>
    row.serves.some((s) => s.coverage === 'new'),
  ).length;
  const noDemand = gainedRows.filter((row) => row.serves.length === 0).map((row) => row.attribute);

  return (
    <section
      className="rounded-2xl border bg-white p-6 shadow-sm"
      data-testid="search-impact-ledger-card"
    >
      <h2 className="text-base font-semibold" data-testid="search-impact-ledger-title">
        Change ledger
      </h2>
      <p className="text-muted-foreground mt-1 max-w-[92ch] text-sm leading-relaxed">
        Every attribute enrichment touched, why it changed, and the search demand it covers. Sorted
        by search volume change.
      </p>

      <div
        className="mt-4 flex flex-wrap gap-2"
        role="group"
        aria-label="Filter by what happened"
        data-testid="search-impact-ledger-filters"
      >
        {LEDGER_FILTERS.map((chip) => (
          <Button
            key={chip.key}
            data-testid={`search-impact-ledger-filter-toggle-${chip.key}`}
            type="button"
            size="sm"
            variant={filter === chip.key ? 'default' : 'outline'}
            aria-pressed={filter === chip.key}
            onClick={() => {
              setFilter(chip.key);
            }}
            className="h-auto cursor-pointer gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold"
          >
            {chip.dot && (
              <span className={cn('h-2 w-2 rounded-full', chip.dot)} aria-hidden="true" />
            )}
            {chip.label}
            <span
              className="font-semibold opacity-75"
              data-testid={`search-impact-ledger-filter-toggle-${chip.key}-value`}
            >
              {ledger.filter((row) => inLedgerFilter(row, chip.key)).length}
            </span>
          </Button>
        ))}
      </div>

      <div
        className="mt-4 overflow-hidden rounded-xl border"
        data-testid="search-impact-ledger-table"
      >
        <div
          className={cn(
            'bg-muted/50 text-muted-foreground hidden gap-4 px-4 py-3 text-xs font-semibold tracking-wider uppercase md:grid',
            GRID,
          )}
        >
          <div>What happened</div>
          <div>Attribute</div>
          <div>Value</div>
          <div>Reason</div>
          <div>Serves</div>
          <div className="text-right">Volume change</div>
        </div>
        {rows.length === 0 ? (
          <p
            className="text-muted-foreground px-4 py-4 text-sm font-medium"
            data-testid="search-impact-ledger-empty"
          >
            Nothing in this group.
          </p>
        ) : (
          rows.map((row) => {
            const reason = REASON_COPY[row.reason];
            const removed = row.verdict === 'lost';
            return (
              <div
                key={row.id}
                data-testid={`search-impact-ledger-row-${row.id}`}
                className={cn(
                  'grid grid-cols-1 items-start gap-2 border-t px-4 py-3.5 first:border-t-0 md:gap-4',
                  GRID,
                )}
              >
                <div>
                  <span
                    data-testid={`search-impact-ledger-row-${row.id}-verdict-badge`}
                    className={cn(
                      'inline-flex rounded-md px-2.5 py-1 text-[11.5px] font-semibold whitespace-nowrap',
                      VERDICT_STYLE[row.verdict].tag,
                    )}
                  >
                    {VERDICT_LABEL[row.verdict]}
                  </span>
                </div>
                <div
                  className="text-sm font-semibold"
                  data-testid={`search-impact-ledger-row-${row.id}-attribute`}
                >
                  {row.attribute}
                  <small className="text-muted-foreground mt-0.5 block text-xs font-semibold">
                    {row.group}
                  </small>
                </div>
                <div className="text-sm leading-relaxed">
                  {row.previous && (
                    <>
                      <span className="text-muted-foreground line-through">{row.previous}</span>
                      {' → '}
                    </>
                  )}
                  <span
                    className={cn('font-semibold', removed && 'text-error')}
                    data-testid={`search-impact-ledger-row-${row.id}-value`}
                  >
                    {removed ? 'Removed' : row.value}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block text-[11px] font-semibold tracking-wider uppercase">
                    {reason.category}
                  </span>
                  <span
                    data-testid={`search-impact-ledger-row-${row.id}-reason-badge`}
                    className={cn(
                      'mt-1 inline-flex rounded-md px-2 py-1 text-xs font-semibold',
                      REASON_TONE_STYLE[reason.tone],
                    )}
                  >
                    {reason.label}
                  </span>
                  {row.evidence && <Quote evidence={row.evidence} />}
                </div>
                <div className="flex flex-col gap-2">
                  {row.serves.length === 0 ? (
                    <span className="text-muted-foreground text-sm">
                      No search demand found (filter and feed attribute)
                    </span>
                  ) : (
                    row.serves.map((demand) => (
                      <Serve
                        key={demand.keyword}
                        demand={demand}
                        testId={`search-impact-ledger-row-${row.id}-serve-item-${demand.keyword}`}
                      />
                    ))
                  )}
                </div>
                <VolumeChange
                  value={row.volumeChange}
                  testId={`search-impact-ledger-row-${row.id}-volume-change-value`}
                />
              </div>
            );
          })
        )}
      </div>

      <div
        className="bg-primary/5 text-primary border-primary/15 mt-4 rounded-xl border px-4 py-3.5 text-sm leading-relaxed"
        data-testid="search-impact-ledger-summary-banner"
      >
        <b className="font-semibold">
          Structured attributes now cover +{formatVolume(demandChange.added)} monthly searches they
          didn&apos;t before
          {demandChange.lost > 0
            ? `, and ${formatVolume(demandChange.lost)} fewer from removed values`
            : ''}
          : net {demandChange.net >= 0 ? '+' : '−'}
          {formatVolume(demandChange.net)}/mo.
        </b>{' '}
        {withDemand} of the {attributes.gained} new attributes carry search demand
        {noDemand.length > 0 ? `; ${noDemand.join(', ')} serves filters and the feed only` : ''}.
      </div>

      <p className="text-muted-foreground mt-3 text-xs leading-relaxed">
        <b className="text-foreground">Serves</b> shows the search demand an attribute&apos;s value
        matches, from Google Ads Keyword Planner: the product descriptors used to write this
        product, and the category&apos;s keyword list.{' '}
        <b className="text-foreground">New coverage</b> means no structured attribute carried that
        demand before. <b className="text-foreground">Volume change</b> counts new coverage only,
        and subtracts demand a removed attribute no longer covers. It shows what the content now
        covers, not what it will drive. <b className="text-foreground">Opportunity</b>: high volume,
        defend at 10K/mo and above; low competition, win below an index of 34; long tail for the
        rest. <b className="text-foreground">Advertiser competition</b> measures paid competition,
        not how hard a term is to rank for. <b className="text-foreground">Commercial intent</b>{' '}
        marks a top-of-page bid above $2.00.
      </p>
    </section>
  );
}
